import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Stack, Switch, InputAdornment,
  FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { employeeApi, payrollApi } from '../../services/employeeService';
import {
  PageHeader, SectionCard, FormSection, FieldGrid, EmptyState, StatusPill,
} from '../../components/ui';
import { tokens } from '../../theme';
import { formatDate, formatMoney, formatPercent, countLabel } from '../../utils/format';

interface Staff { id: string; firstName: string; lastName: string; }
interface RuleConfigJson {
  useBillableTime?: boolean;
  baseMonthly?: { amount?: number };
  hourly?: { rate?: number };
  serviceCommission?: { pct?: number; materialReducesBase?: boolean };
  productCommission?: { pct?: number };
  bonus?: { amount?: number };
  deductions?: { amount?: number };
  employeeZus?: { pct?: number };
  employerZus?: { pct?: number };
  otherEmployerCost?: { amount?: number };
}
interface RuleVersion {
  id: string; effectiveFrom: string;
  baseMonthly: number; hourlyRate: number; serviceCommissionPct: number; productCommissionPct: number;
  config?: RuleConfigJson;
}
interface Rule { id: string; name: string; isActive: boolean; versions: RuleVersion[]; }
// Szablon = najnowsza wersja zasad dowolnego pracownika, do ponownego użycia bez przepisywania
interface RuleTemplate { id: string; name: string; label: string; config: RuleConfigJson; }

const num = (v: string) => Number(v) || 0;

const defaultForm = () => ({
  name: 'Zasady wynagrodzenia',
  effectiveFrom: `${new Date().getFullYear()}-01-01`,
  baseMonthly: '0', hourlyRate: '28', servicePct: '15', productPct: '10', bonus: '0', deductions: '0',
  employeeZusPct: '13.71', employerZusPct: '20.48', otherEmployerCost: '0',
  useBillable: true, materialReducesBase: false,
});

type FormState = ReturnType<typeof defaultForm>;
type NumericKey = 'baseMonthly' | 'hourlyRate' | 'servicePct' | 'productPct' | 'bonus' | 'deductions' | 'employeeZusPct' | 'employerZusPct' | 'otherEmployerCost';

interface FieldDef { key: NumericKey; label: string; unit: string; helper?: string; step?: number }

const RATE_FIELDS: FieldDef[] = [
  { key: 'baseMonthly', label: 'Podstawa miesięczna', unit: 'zł', helper: 'Stała kwota brutto co miesiąc' },
  { key: 'hourlyRate', label: 'Stawka godzinowa', unit: 'zł/h', helper: 'Za każdą przepracowaną godzinę', step: 0.5 },
  { key: 'servicePct', label: 'Prowizja od usług', unit: '%', helper: 'Od sprzedaży usług pracownika', step: 0.5 },
  { key: 'productPct', label: 'Prowizja od produktów', unit: '%', helper: 'Od sprzedaży produktów pracownika', step: 0.5 },
  { key: 'bonus', label: 'Bonus', unit: 'zł', helper: 'Dodatek doliczany co miesiąc' },
  { key: 'deductions', label: 'Potrącenia', unit: 'zł', helper: 'Odejmowane od wynagrodzenia' },
];

const COST_FIELDS: FieldDef[] = [
  { key: 'employeeZusPct', label: 'ZUS pracownika', unit: '%', helper: 'Potrącany z wynagrodzenia brutto', step: 0.01 },
  { key: 'employerZusPct', label: 'ZUS pracodawcy', unit: '%', helper: 'Doliczany do kosztu salonu', step: 0.01 },
  { key: 'otherEmployerCost', label: 'Inne koszty pracodawcy', unit: 'zł', helper: 'Np. szkolenia, odzież, benefity' },
];

const SwitchRow: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; description: string }> = ({ checked, onChange, label, description }) => (
  <FormControlLabel
    sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
    control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ mt: 0.25, flexShrink: 0 }} />}
    label={(
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{description}</Typography>
      </Box>
    )}
  />
);

const cellNum = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export const PayrollRulesPage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [staffId, setStaffId] = useState('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [templates, setTemplates] = useState<RuleTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const [form, setForm] = useState(defaultForm());

  useEffect(() => {
    if (!activeSalonId) return;
    employeeApi.list(activeSalonId).then((rows: Staff[]) => { setStaff(rows); if (rows[0]) setStaffId(rows[0].id); });
  }, [activeSalonId]);

  const loadRules = useCallback(async () => {
    if (!staffId) return;
    try { setRules(await payrollApi.listRules(staffId) as Rule[]); } catch { setRules([]); }
  }, [staffId]);
  useEffect(() => { loadRules(); }, [loadRules]);

  // Zasady wszystkich pracowników salonu jako szablony do ponownego użycia
  const loadTemplates = useCallback(async () => {
    if (staff.length === 0) { setTemplates([]); return; }
    const all = await Promise.all(staff.map(async (s) => {
      try { return { s, rules: await payrollApi.listRules(s.id) as Rule[] }; }
      catch { return { s, rules: [] as Rule[] }; }
    }));
    const list: RuleTemplate[] = [];
    all.forEach(({ s, rules: rs }) => rs.forEach((r) => {
      const latest = r.versions[0];
      if (!latest?.config) return;
      list.push({ id: r.id, name: r.name, label: `${r.name} — ${s.firstName} ${s.lastName}`, config: latest.config });
    }));
    setTemplates(list);
  }, [staff]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    if (!id) { setForm(defaultForm()); return; }
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const c = t.config;
    setForm((f) => ({
      ...f,
      name: t.name,
      baseMonthly: String(c.baseMonthly?.amount ?? 0),
      hourlyRate: String(c.hourly?.rate ?? 0),
      servicePct: String(c.serviceCommission?.pct ?? 0),
      productPct: String(c.productCommission?.pct ?? 0),
      bonus: String(c.bonus?.amount ?? 0),
      deductions: String(c.deductions?.amount ?? 0),
      employeeZusPct: String(c.employeeZus?.pct ?? 0),
      employerZusPct: String(c.employerZus?.pct ?? 0),
      otherEmployerCost: String(c.otherEmployerCost?.amount ?? 0),
      useBillable: c.useBillableTime ?? true,
      materialReducesBase: c.serviceCommission?.materialReducesBase ?? false,
    }));
  };

  const save = async () => {
    if (!staffId) return;
    const config = {
      useBillableTime: form.useBillable,
      baseMonthly: { active: num(form.baseMonthly) > 0, amount: num(form.baseMonthly) },
      hourly: { active: num(form.hourlyRate) > 0, rate: num(form.hourlyRate) },
      serviceCommission: { active: num(form.servicePct) > 0, pct: num(form.servicePct), base: 'GROSS', materialReducesBase: form.materialReducesBase },
      productCommission: { active: num(form.productPct) > 0, pct: num(form.productPct) },
      bonus: { active: num(form.bonus) > 0, amount: num(form.bonus) },
      deductions: { active: num(form.deductions) > 0, amount: num(form.deductions) },
      employeeZus: { active: num(form.employeeZusPct) > 0, pct: num(form.employeeZusPct) },
      employerZus: { active: num(form.employerZusPct) > 0, pct: num(form.employerZusPct) },
      otherEmployerCost: { active: num(form.otherEmployerCost) > 0, amount: num(form.otherEmployerCost) },
    };
    try {
      await payrollApi.upsertRule(staffId, { name: form.name, effectiveFrom: form.effectiveFrom, config });
      setToast({ msg: 'Zapisano nową wersję zasad (obowiązuje od podanej daty)', sev: 'success' });
      loadRules();
      loadTemplates();
    } catch (e) {
      setToast({ msg: (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać zasad. Sprawdź pola i spróbuj ponownie.', sev: 'error' });
    }
  };

  const numField = (f: FieldDef) => (
    <TextField
      key={f.key}
      fullWidth
      type="number"
      label={f.label}
      value={form[f.key]}
      onChange={(e) => setForm({ ...form, [f.key]: e.target.value } as FormState)}
      InputProps={{ endAdornment: <InputAdornment position="end">{f.unit}</InputAdornment> }}
      inputProps={{ min: 0, step: f.step ?? 1, inputMode: 'decimal' }}
      helperText={f.helper}
    />
  );

  const currentStaff = staff.find((s) => s.id === staffId);
  const versionCount = rules.reduce((n, r) => n + r.versions.length, 0);

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Wynagrodzenia' }]}
        title="Zasady wynagrodzeń"
        description="Stawki, prowizje i składki każdego pracownika. Każdy zapis tworzy nową wersję, obowiązującą od wybranej daty; poprzednie wersje zostają w historii."
      />

      <Stack gap={2.5}>
        <SectionCard title="Nowa wersja zasad">
          <FormSection first sx={{ pt: { xs: 0, md: 0 } }} title="Pracownik i szablon" description="Wybierz osobę. Szablon wypełni formularz zasadami innego pracownika.">
            <FieldGrid columns={2}>
              <TextField select fullWidth label="Pracownik" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                {staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>)}
              </TextField>
              <TextField
                select
                fullWidth
                label="Szablon"
                value={templateId}
                onChange={(e) => applyTemplate(e.target.value)}
                SelectProps={{ displayEmpty: true }}
                helperText="Nie musisz wpisywać stawek od nowa"
              >
                <MenuItem value="">Nowe zasady od zera</MenuItem>
                {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
              </TextField>
              <TextField fullWidth label="Nazwa zasad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField
                fullWidth
                label="Obowiązuje od"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                helperText="Rozliczenia od tego dnia użyją nowych stawek"
                sx={{ minWidth: 170 }}
              />
            </FieldGrid>
          </FormSection>

          <FormSection title="Stawki" description="Składniki wynagrodzenia. Zostaw 0, jeśli składnik nie dotyczy tej osoby.">
            <FieldGrid columns={2}>
              {RATE_FIELDS.map(numField)}
            </FieldGrid>
          </FormSection>

          <FormSection title="Składki i koszty" description="Obciążenia po stronie pracownika i salonu.">
            <FieldGrid columns={2}>
              {COST_FIELDS.map(numField)}
            </FieldGrid>
          </FormSection>

          <FormSection title="Sposób rozliczania" description="Od czego liczyć stawkę godzinową i prowizję.">
            <Stack gap={2}>
              <SwitchRow
                checked={form.useBillable}
                onChange={(v) => setForm({ ...form, useBillable: v })}
                label="Rozliczaj według czasu rozliczeniowego"
                description="Wyłączone — godziny liczone według czasu faktycznego."
              />
              <SwitchRow
                checked={form.materialReducesBase}
                onChange={(v) => setForm({ ...form, materialReducesBase: v })}
                label="Materiał obniża podstawę prowizji"
                description="Koszt zużytych materiałów odejmowany od sprzedaży usług przed naliczeniem prowizji."
              />
            </Stack>
          </FormSection>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            gap={1.5}
            sx={{ borderTop: `1px solid ${tokens.line}`, pt: 2.5 }}
          >
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              Poprzednie wersje zostają w historii poniżej.
            </Typography>
            <Button variant="contained" onClick={save} disabled={!staffId}>Zapisz nową wersję</Button>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Historia wersji"
          icon={<HistoryOutlinedIcon />}
          subtitle={currentStaff ? `${currentStaff.firstName} ${currentStaff.lastName}${versionCount ? ` · ${countLabel(versionCount, 'wersja', 'wersje', 'wersji')}` : ''}` : undefined}
          padding="none"
        >
          {rules.length === 0 ? (
            <EmptyState
              compact
              title="Ten pracownik nie ma jeszcze zasad wynagrodzenia"
              description="Uzupełnij formularz powyżej i zapisz pierwszą wersję."
            />
          ) : (
            rules.map((r, ri) => (
              <Box key={r.id} sx={{ borderTop: ri > 0 ? `1px solid ${tokens.line}` : 'none' }}>
                {(rules.length > 1 || r.isActive) && (
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: { xs: 2, md: 2.5 }, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{r.name}</Typography>
                    {r.isActive ? <StatusPill size="small" tone="success" label="Aktywne" /> : <StatusPill size="small" tone="neutral" label="Nieaktywne" />}
                  </Stack>
                )}
                <TableContainer>
                  <Table sx={{ minWidth: 620 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Obowiązuje od</TableCell>
                        <TableCell align="right">Podstawa</TableCell>
                        <TableCell align="right">Stawka godzinowa</TableCell>
                        <TableCell align="right">Prowizja od usług</TableCell>
                        <TableCell align="right">Prowizja od produktów</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.versions.map((v, vi) => (
                        <TableRow key={v.id} hover>
                          <TableCell sx={cellNum}>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <span>{formatDate(v.effectiveFrom)}</span>
                              {vi === 0 && r.versions.length > 1 && <StatusPill size="small" tone="plum" noDot label="Najnowsza" />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={cellNum}>{formatMoney(v.baseMonthly)}</TableCell>
                          <TableCell align="right" sx={cellNum}>{formatMoney(v.hourlyRate)}/h</TableCell>
                          <TableCell align="right" sx={cellNum}>{formatPercent(v.serviceCommissionPct, Number.isInteger(Number(v.serviceCommissionPct)) ? 0 : 1)}</TableCell>
                          <TableCell align="right" sx={cellNum}>{formatPercent(v.productCommissionPct, Number.isInteger(Number(v.productCommissionPct)) ? 0 : 1)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          )}
        </SectionCard>
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default PayrollRulesPage;
